import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Pencil, 
  FolderTree, 
  Layers, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowUpDown,
  GripVertical,
  Database,
  Settings,
  ChevronRight,
  ChevronDown,
  Package,
  CheckSquare,
  X,
  Tag,
  Search,
  Trash2,
  Archive
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
// iter-364 — Featured Brand Partner placement on BOQ Categories page.
import SponsorBadge from '@/components/SponsorBadge';

const API = '/api';

const BOQCategoryLibrary = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('main');
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [needsCategoryTasks, setNeedsCategoryTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  
  // NEW: Search and Expand states
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Dialog states
  const [mainCategoryDialog, setMainCategoryDialog] = useState(false);
  const [subCategoryDialog, setSubCategoryDialog] = useState(false);
  const [editingMainCategory, setEditingMainCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  
  // Category Assignment Dialog
  const [assignCategoryDialog, setAssignCategoryDialog] = useState(false);
  const [selectedTasksForAssign, setSelectedTasksForAssign] = useState([]);
  const [assignForm, setAssignForm] = useState({ main_category_id: '', sub_category_id: '' });
  const [assigning, setAssigning] = useState(false);
  
  // Form states
  const [mainForm, setMainForm] = useState({ code: '', name: '', display_order: 99, status: 'active' });
  const [subForm, setSubForm] = useState({ code: '', name: '', main_category_id: '', display_order: 99, status: 'active' });
  
  // Selected main category for filtering sub-categories
  const [selectedMainCategoryFilter, setSelectedMainCategoryFilter] = useState('all');
  
  // Drag and drop state
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);

  // iter-359h — Merge Sub-Categories UI state.
  // selectedForMerge: { mainCategoryId → Set<subCategoryId> }
  const [selectedForMerge, setSelectedForMerge] = useState({});
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeMainCategoryId, setMergeMainCategoryId] = useState(null);
  const [mergeMasterId, setMergeMasterId] = useState('');
  const [merging, setMerging] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const isAdmin = user.role === 'owner' || user.role === 'admin';

  // Debounced color update
  const colorTimers = useRef({});
  const handleColorChange = useCallback((catId, newColor) => {
    // Optimistic local state update
    setMainCategories(prev => prev.map(c => c.id === catId ? { ...c, color: newColor } : c));
    // Debounce the API call
    if (colorTimers.current[catId]) clearTimeout(colorTimers.current[catId]);
    colorTimers.current[catId] = setTimeout(async () => {
      try {
        await axios.put(`${API}/boq-categories/main/${catId}`, { color: newColor }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {
        toast.error('Failed to save color');
      }
    }, 500);
  }, [token]);

  // Toggle expand/collapse for a main category
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Expand all categories
  const expandAll = () => {
    const expanded = {};
    mainCategories.forEach(cat => {
      expanded[cat.id] = true;
    });
    setExpandedCategories(expanded);
  };

  // Collapse all categories
  const collapseAll = () => {
    setExpandedCategories({});
  };

  // Get sub-categories for a main category
  const getSubCategoriesForMain = (mainCategoryId) => {
    return subCategories.filter(sub => sub.main_category_id === mainCategoryId)
      .sort((a, b) => (a.display_order || 99) - (b.display_order || 99));
  };

  // Filter categories based on search
  const filteredMainCategories = mainCategories.filter(cat => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    // Check main category
    if (cat.code?.toLowerCase().includes(query) || cat.name?.toLowerCase().includes(query)) {
      return true;
    }
    // Check if any sub-category matches
    const subs = getSubCategoriesForMain(cat.id);
    return subs.some(sub => 
      sub.code?.toLowerCase().includes(query) || sub.name?.toLowerCase().includes(query)
    );
  });

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [mainRes, subRes, tasksRes] = await Promise.all([
        axios.get(`${API}/boq-categories/main`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/boq-categories/sub`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tasks/needs-category`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setMainCategories(mainRes.data);
      setSubCategories(subRes.data);
      setNeedsCategoryTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      // If no categories exist, show empty state
      if (error.response?.status === 404 || mainCategories.length === 0) {
        setMainCategories([]);
        setSubCategories([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeedCategories = async () => {
    try {
      setSeeding(true);
      await axios.post(`${API}/boq-categories/seed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Categories seeded successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed categories');
    } finally {
      setSeeding(false);
    }
  };

  // Main Category CRUD
  const openMainCategoryDialog = (category = null) => {
    if (category) {
      setEditingMainCategory(category);
      setMainForm({
        code: category.code,
        name: category.name,
        display_order: category.display_order,
        status: category.status
      });
    } else {
      setEditingMainCategory(null);
      setMainForm({ code: '', name: '', display_order: mainCategories.length + 1, status: 'active' });
    }
    setMainCategoryDialog(true);
  };

  const saveMainCategory = async () => {
    try {
      if (editingMainCategory) {
        await axios.put(`${API}/boq-categories/main/${editingMainCategory.id}`, {
          name: mainForm.name,
          display_order: mainForm.display_order,
          status: mainForm.status
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/boq-categories/main`, mainForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category created');
      }
      setMainCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  // Sub Category CRUD
  const openSubCategoryDialog = async (category = null) => {
    if (category) {
      setEditingSubCategory(category);
      setSubForm({
        code: category.code,
        name: category.name,
        main_category_id: category.main_category_id,
        display_order: category.display_order,
        status: category.status
      });
    } else {
      setEditingSubCategory(null);
      const defaultMainId = selectedMainCategoryFilter !== 'all' ? selectedMainCategoryFilter : (mainCategories[0]?.id || '');
      
      // Auto-fetch next available code if main category is selected
      let nextCode = '';
      if (defaultMainId) {
        try {
          const response = await axios.get(`${API}/boq-categories/sub/next-code/${defaultMainId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          nextCode = response.data.next_code || '';
        } catch (error) {
          console.log('Could not fetch next code');
        }
      }
      
      setSubForm({ code: nextCode, name: '', main_category_id: defaultMainId, display_order: 99, status: 'active' });
    }
    setSubCategoryDialog(true);
  };
  
  // Fetch next code when main category changes in sub-category form
  const handleMainCategoryChange = async (mainCatId) => {
    setSubForm(prev => ({ ...prev, main_category_id: mainCatId }));
    
    // Auto-fetch next available code
    if (mainCatId && !editingSubCategory) {
      try {
        const response = await axios.get(`${API}/boq-categories/sub/next-code/${mainCatId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubForm(prev => ({ ...prev, code: response.data.next_code || '' }));
      } catch (error) {
        console.log('Could not fetch next code');
      }
    }
  };

  const saveSubCategory = async () => {
    try {
      if (editingSubCategory) {
        await axios.put(`${API}/boq-categories/sub/${editingSubCategory.id}`, {
          name: subForm.name,
          display_order: subForm.display_order,
          status: subForm.status
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Sub-category updated');
      } else {
        await axios.post(`${API}/boq-categories/sub`, subForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Sub-category created');
      }
      setSubCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sub-category');
    }
  };

  // ========== CATEGORY ASSIGNMENT FUNCTIONS ==========
  
  // Toggle task selection for bulk assign
  const toggleTaskSelection = (taskId) => {
    setSelectedTasksForAssign(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      }
      return [...prev, taskId];
    });
  };
  
  // Select all tasks
  const selectAllTasks = () => {
    if (selectedTasksForAssign.length === needsCategoryTasks.length) {
      setSelectedTasksForAssign([]);
    } else {
      setSelectedTasksForAssign(needsCategoryTasks.map(t => t.id));
    }
  };
  
  // Open assign category dialog for single task
  const openAssignDialog = (task = null) => {
    if (task) {
      setSelectedTasksForAssign([task.id]);
    }
    setAssignForm({ main_category_id: '', sub_category_id: '' });
    setAssignCategoryDialog(true);
  };
  
  // Open assign dialog for bulk
  const openBulkAssignDialog = () => {
    if (selectedTasksForAssign.length === 0) {
      toast.error('Please select at least one task');
      return;
    }
    setAssignForm({ main_category_id: '', sub_category_id: '' });
    setAssignCategoryDialog(true);
  };
  
  // Get filtered sub-categories for assignment dialog
  const getFilteredSubCategoriesForAssign = () => {
    if (!assignForm.main_category_id) return [];
    return subCategories.filter(s => s.main_category_id === assignForm.main_category_id);
  };
  
  // Delete main category
  const deleteMainCategory = async (categoryId, categoryCode, usageCount = 0) => {
    if (usageCount > 0) {
      // Category is in use - offer force delete (soft-delete/archive)
      const confirmArchive = window.confirm(
        `Category "${categoryCode}" is used by ${usageCount} tasks.\n\n` +
        `Would you like to ARCHIVE it instead? Tasks will keep their categorization, ` +
        `but the category won't appear for new tasks.`
      );
      if (!confirmArchive) return;
      
      try {
        const response = await axios.delete(`${API}/boq-categories/main/${categoryId}?force=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(response.data.message);
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to archive category');
      }
    } else {
      // Category not in use - normal delete
      if (!window.confirm(`Are you sure you want to delete "${categoryCode}" and all its sub-categories? This cannot be undone.`)) {
        return;
      }
      try {
        await axios.delete(`${API}/boq-categories/main/${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Category "${categoryCode}" deleted successfully`);
        fetchCategories();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete category');
      }
    }
  };
  
  // Move category up/down
  const moveCategory = async (direction, categoryId, categoryType = 'main') => {
    const categories = categoryType === 'main' ? mainCategories : subCategories;
    const currentIndex = categories.findIndex(c => c.id === categoryId);
    if (currentIndex < 0) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    // Swap the two items
    const newOrder = [...categories];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    // Update display order via API
    try {
      const endpoint = categoryType === 'main' 
        ? `${API}/boq-categories/main/reorder`
        : `${API}/boq-categories/sub/reorder`;
      
      await axios.post(endpoint, {
        order: newOrder.map(c => c.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Update local state immediately
      if (categoryType === 'main') {
        setMainCategories(newOrder);
      } else {
        setSubCategories(newOrder);
      }
      toast.success('Category order updated');
    } catch (error) {
      toast.error('Failed to reorder categories');
    }
  };
  
  // Drag and Drop handlers for categories
  const handleDragStart = (e, category, categoryType) => {
    setDraggedCategory({ ...category, type: categoryType });
    e.dataTransfer.effectAllowed = 'move';
    // Add a ghost effect
    e.target.style.opacity = '0.5';
  };
  
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedCategory(null);
    setDragOverCategory(null);
  };
  
  const handleDragOver = (e, category) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedCategory && draggedCategory.id !== category.id) {
      setDragOverCategory(category.id);
    }
  };
  
  const handleDragLeave = () => {
    setDragOverCategory(null);
  };
  
  const handleDrop = async (e, targetCategory, categoryType) => {
    e.preventDefault();
    setDragOverCategory(null);
    
    if (!draggedCategory || draggedCategory.type !== categoryType || draggedCategory.id === targetCategory.id) {
      return;
    }
    
    const categories = categoryType === 'main' ? [...mainCategories] : [...subCategories];
    const dragIndex = categories.findIndex(c => c.id === draggedCategory.id);
    const dropIndex = categories.findIndex(c => c.id === targetCategory.id);
    
    if (dragIndex < 0 || dropIndex < 0) return;
    
    // Remove dragged item and insert at new position
    const [removed] = categories.splice(dragIndex, 1);
    categories.splice(dropIndex, 0, removed);
    
    // Update local state immediately for smooth UX
    if (categoryType === 'main') {
      setMainCategories(categories);
    } else {
      setSubCategories(categories);
    }
    
    // Save to backend
    try {
      const endpoint = categoryType === 'main' 
        ? `${API}/boq-categories/main/reorder`
        : `${API}/boq-categories/sub/reorder`;
      
      await axios.post(endpoint, {
        order: categories.map(c => c.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Category order updated');
    } catch (error) {
      // Revert on error
      fetchCategories();
      toast.error('Failed to reorder categories');
    }
  };
  
  // Delete sub-category
  const deleteSubCategory = async (categoryId, categoryCode) => {
    if (!window.confirm(`Are you sure you want to delete sub-category "${categoryCode}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/boq-categories/sub/${categoryId}`);
      toast.success(`Sub-category "${categoryCode}" deleted successfully`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-category');
    }
  };

  // iter-359h — merge-sub-categories helpers.
  const toggleMergeSelection = (mainId, subId) => {
    setSelectedForMerge((prev) => {
      const current = new Set(prev[mainId] || []);
      if (current.has(subId)) current.delete(subId); else current.add(subId);
      return { ...prev, [mainId]: current };
    });
  };

  const clearMergeSelection = (mainId) => {
    setSelectedForMerge((prev) => ({ ...prev, [mainId]: new Set() }));
  };

  const openMergeDialog = (mainId) => {
    const picked = Array.from(selectedForMerge[mainId] || []);
    if (picked.length < 2) {
      toast.error('Select at least 2 sub-categories to merge');
      return;
    }
    setMergeMainCategoryId(mainId);
    setMergeMasterId(picked[0]);
    setMergeDialogOpen(true);
  };

  const submitMerge = async () => {
    const picked = Array.from(selectedForMerge[mergeMainCategoryId] || []);
    const losers = picked.filter((id) => id !== mergeMasterId);
    if (!mergeMasterId || losers.length === 0) {
      toast.error('Pick a master and at least one loser'); return;
    }
    setMerging(true);
    try {
      const res = await axios.post(`${API}/boq-categories/sub/merge`, {
        master_id: mergeMasterId,
        loser_ids: losers,
      });
      toast.success(res.data.message || 'Merge complete');
      setMergeDialogOpen(false);
      clearMergeSelection(mergeMainCategoryId);
      setMergeMasterId('');
      setMergeMainCategoryId(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };
  
  // Save category assignment
  const saveAssignment = async () => {
    if (!assignForm.main_category_id || !assignForm.sub_category_id) {
      toast.error('Please select both main and sub-category');
      return;
    }
    
    try {
      setAssigning(true);
      const response = await axios.post(`${API}/tasks/assign-category`, {
        task_ids: selectedTasksForAssign,
        main_category_id: assignForm.main_category_id,
        sub_category_id: assignForm.sub_category_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(response.data.message || `Category assigned to ${selectedTasksForAssign.length} task(s)`);
      setAssignCategoryDialog(false);
      setSelectedTasksForAssign([]);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign category');
    } finally {
      setAssigning(false);
    }
  };

  // Filter sub-categories based on selected main category
  const filteredSubCategories = selectedMainCategoryFilter === 'all' 
    ? subCategories 
    : subCategories.filter(s => s.main_category_id === selectedMainCategoryFilter);

  // Get main category name by ID
  const getMainCategoryName = (mainCatId) => {
    const mainCat = mainCategories.find(c => c.id === mainCatId);
    return mainCat ? `${mainCat.code} – ${mainCat.name}` : 'Unknown';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* iter-364 — Sponsored placement (zone=boq_categories). */}
        <SponsorBadge zone="boq_categories" limit={1} variant="premium-minimal" />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-playfair text-3xl font-bold text-stone-900">BOQ Category Library</h1>
            <p className="text-stone-500 mt-1">Manage main categories and sub-categories for BOQ organization</p>
          </div>
          {mainCategories.length === 0 && (
            <Button onClick={handleSeedCategories} disabled={seeding} className="bg-stone-800 hover:bg-stone-900">
              <Database size={16} className="mr-2" />
              {seeding ? 'Seeding...' : 'Initialize Categories'}
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FolderTree className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{mainCategories.length}</p>
                <p className="text-sm text-stone-500">Main Categories</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Layers className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{subCategories.length}</p>
                <p className="text-sm text-stone-500">Sub-Categories</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{mainCategories.filter(c => c.status === 'active').length}</p>
                <p className="text-sm text-stone-500">Active Categories</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4 cursor-pointer hover:border-stone-300 transition-colors" onClick={() => setActiveTab('needs-category')}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${needsCategoryTasks.length > 0 ? 'bg-stone-100' : 'bg-stone-100'} flex items-center justify-center`}>
                <AlertTriangle className={needsCategoryTasks.length > 0 ? 'text-stone-700' : 'text-stone-400'} size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-900">{needsCategoryTasks.length}</p>
                <p className="text-sm text-stone-500">Needs Category</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="main" className="data-[state=active]:bg-white">
              <FolderTree size={16} className="mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="needs-category" className="data-[state=active]:bg-white">
              <AlertTriangle size={16} className="mr-2" />
              Needs Category ({needsCategoryTasks.length})
            </TabsTrigger>
          </TabsList>

          {/* Main Categories Tab - Now with expandable sub-categories */}
          <TabsContent value="main" className="mt-4">
            <div className="bg-white rounded-xl border border-stone-200">
              {/* Header with Search */}
              <div className="p-4 border-b border-stone-200">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h2 className="font-semibold text-stone-900">Category Library</h2>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={expandAll}>
                      Expand All
                    </Button>
                    <Button variant="outline" size="sm" onClick={collapseAll}>
                      Collapse All
                    </Button>
                    <Button size="sm" onClick={() => openMainCategoryDialog()} className="bg-stone-800 hover:bg-stone-900">
                      <Plus size={16} className="mr-1" />
                      Add Main Category
                    </Button>
                  </div>
                </div>
                {/* Search Box */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Search categories (e.g., Civil, A01, Electrical...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full md:w-96"
                    data-testid="category-search-input"
                  />
                </div>
              </div>
              
              {/* Category List with Expandable Sub-categories */}
              <div className="divide-y divide-stone-100">
                {filteredMainCategories.map((cat) => {
                  const subCats = getSubCategoriesForMain(cat.id);
                  const isExpanded = expandedCategories[cat.id];
                  const isDragOver = dragOverCategory === cat.id;
                  
                  return (
                    <div key={cat.id} className="border-b border-stone-100 last:border-b-0">
                      {/* Main Category Row - Draggable */}
                      <div 
                        className={`p-4 flex items-center justify-between hover:bg-stone-50 cursor-grab active:cursor-grabbing transition-all ${
                          isDragOver ? 'bg-blue-50 border-t-2 border-blue-400' : ''
                        }`}
                        draggable
                        data-cat-id={cat.id}
                        onDragStart={(e) => handleDragStart(e, cat, 'main')}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, cat)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, cat, 'main')}
                        onClick={() => toggleCategory(cat.id)}
                      >
                        <div className="flex items-center gap-4">
                          {/* Drag Handle - with touch support */}
                          <div className="flex items-center gap-2">
                            <div className="cursor-grab text-stone-400 hover:text-stone-600" title="Drag to reorder" data-drag-grip="true"
                              onTouchStart={(e) => {
                                e.preventDefault();
                                window._touchDragCat = { id: cat.id };
                              }}
                              onTouchMove={(e) => {
                                if (!window._touchDragCat) return;
                                e.preventDefault();
                                const touch = e.touches[0];
                                const el = document.elementFromPoint(touch.clientX, touch.clientY);
                                const target = el?.closest('[data-cat-id]');
                                if (target) window._touchDropCat = target.getAttribute('data-cat-id');
                              }}
                              onTouchEnd={() => {
                                if (window._touchDragCat && window._touchDropCat && window._touchDragCat.id !== window._touchDropCat) {
                                  handleDrop({ preventDefault: () => {}, stopPropagation: () => {} }, { id: window._touchDropCat }, 'main');
                                }
                                window._touchDragCat = null;
                                window._touchDropCat = null;
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <circle cx="4" cy="4" r="1.5"/>
                                <circle cx="4" cy="8" r="1.5"/>
                                <circle cx="4" cy="12" r="1.5"/>
                                <circle cx="10" cy="4" r="1.5"/>
                                <circle cx="10" cy="8" r="1.5"/>
                                <circle cx="10" cy="12" r="1.5"/>
                              </svg>
                            </div>
                            <button className="p-1 hover:bg-stone-200 rounded transition-colors">
                              {isExpanded ? (
                                <ChevronDown size={20} className="text-stone-500" />
                              ) : (
                                <ChevronRight size={20} className="text-stone-500" />
                              )}
                            </button>
                          </div>
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white" style={{ backgroundColor: cat.color || '#9CA3AF' }}>
                            {cat.code}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{cat.name}</p>
                            <p className="text-xs text-stone-500">
                              {subCats.length} sub-categories | Order: {cat.display_order} | Used: {cat.usage_count || 0} times
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="color"
                            value={cat.color || '#9CA3AF'}
                            onChange={(e) => handleColorChange(cat.id, e.target.value)}
                            className="w-8 h-8 rounded cursor-pointer border border-stone-300"
                            title="Category color"
                            data-testid={`cat-color-picker-${cat.code}`}
                          />
                          <Badge variant={cat.status === 'active' ? 'default' : cat.status === 'deleted' ? 'destructive' : 'secondary'} className={cat.status === 'active' ? 'bg-green-100 text-green-700' : cat.status === 'deleted' ? 'bg-red-100 text-red-600' : ''}>
                            {cat.status === 'deleted' ? 'archived' : cat.status}
                          </Badge>
                          <Button variant="ghost" size="sm" onClick={() => openMainCategoryDialog(cat)}>
                            <Pencil size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => deleteMainCategory(cat.id, cat.code, cat.usage_count || 0)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            title={cat.usage_count > 0 ? "Archive (category in use)" : "Delete"}
                          >
                            {cat.usage_count > 0 ? <Archive size={14} /> : <Trash2 size={14} />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openSubCategoryDialog()}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Plus size={14} className="mr-1" />
                            Add Sub
                          </Button>
                        </div>
                      </div>
                      
                      {/* Sub-categories (Expandable) */}
                      {isExpanded && subCats.length > 0 && (
                        <div className="bg-stone-50 border-t border-stone-100">
                          {/* iter-359h — bulk-merge action bar */}
                          {(() => {
                            const pickedIds = Array.from(selectedForMerge[cat.id] || []);
                            if (pickedIds.length < 1) return null;
                            return (
                              <div
                                className="ml-14 px-4 py-2 flex items-center justify-between bg-amber-50 border-b border-amber-200 text-xs"
                                data-testid={`merge-action-bar-${cat.code}`}
                              >
                                <span className="text-amber-800 font-medium">
                                  {pickedIds.length} sub-categor{pickedIds.length === 1 ? 'y' : 'ies'} selected
                                </span>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[11px] text-stone-600 hover:bg-stone-100"
                                    onClick={() => clearMergeSelection(cat.id)}
                                    data-testid={`merge-clear-${cat.code}`}
                                  >
                                    Clear
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                                    onClick={() => openMergeDialog(cat.id)}
                                    disabled={pickedIds.length < 2}
                                    data-testid={`merge-btn-${cat.code}`}
                                  >
                                    Merge into one
                                  </Button>
                                </div>
                              </div>
                            );
                          })()}
                          {subCats.map((sub, idx) => {
                            const isPicked = (selectedForMerge[cat.id] || new Set()).has(sub.id);
                            return (
                            <div 
                              key={sub.id} 
                              className={`px-4 py-3 flex items-center justify-between hover:bg-stone-100 ml-14 border-l-2 ${isPicked ? 'border-amber-400 bg-amber-50/40' : 'border-stone-200'}`}
                            >
                              <div className="flex items-center gap-3">
                                {isAdmin && (
                                  <input
                                    type="checkbox"
                                    checked={isPicked}
                                    onChange={() => toggleMergeSelection(cat.id, sub.id)}
                                    className="h-3.5 w-3.5 accent-amber-600 cursor-pointer"
                                    title="Select for merge"
                                    data-testid={`merge-check-${sub.code}`}
                                  />
                                )}
                                <div className="w-12 h-8 rounded bg-blue-50 flex items-center justify-center font-mono text-xs font-semibold text-blue-700">
                                  {sub.code}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-stone-800">{sub.name}</p>
                                  <p className="text-xs text-stone-400">Order: {sub.display_order}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant="secondary" 
                                  className={`text-xs ${sub.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-stone-100 text-stone-500'}`}
                                >
                                  {sub.status}
                                </Badge>
                                <Button variant="ghost" size="sm" onClick={() => openSubCategoryDialog(sub)}>
                                  <Pencil size={12} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => deleteSubCategory(sub.id, sub.code)}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Empty sub-categories message */}
                      {isExpanded && subCats.length === 0 && (
                        <div className="bg-stone-50 border-t border-stone-100 p-4 ml-14 text-sm text-stone-500">
                          No sub-categories yet. Click "Add Sub" to create one.
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {filteredMainCategories.length === 0 && searchQuery && (
                  <div className="p-8 text-center text-stone-500">
                    <Search size={40} className="mx-auto mb-3 text-stone-300" />
                    <p>No categories found for "{searchQuery}"</p>
                    <p className="text-sm mt-1">Try a different search term</p>
                  </div>
                )}
                
                {mainCategories.length === 0 && (
                  <div className="p-8 text-center text-stone-500">
                    <FolderTree size={40} className="mx-auto mb-3 text-stone-300" />
                    <p>No main categories yet.</p>
                    <p className="text-sm">Click "Initialize Categories" to seed default categories.</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Needs Category Tab */}
          <TabsContent value="needs-category" className="mt-4">
            <div className="bg-white rounded-xl border border-stone-200">
              <div className="p-4 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-stone-900">Tasks Needing Category Assignment</h2>
                  <p className="text-sm text-stone-500 mt-1">These tasks will appear under "Others" in BOQ exports until categorized.</p>
                </div>
                {needsCategoryTasks.length > 0 && (
                  <div className="flex items-center gap-2">
                    {selectedTasksForAssign.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700">{selectedTasksForAssign.length} selected</Badge>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={selectAllTasks}
                    >
                      <CheckSquare size={16} className="mr-1" />
                      {selectedTasksForAssign.length === needsCategoryTasks.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={openBulkAssignDialog}
                      disabled={selectedTasksForAssign.length === 0}
                      className="bg-stone-800 hover:bg-stone-900"
                    >
                      <Tag size={16} className="mr-1" />
                      Bulk Assign Category
                    </Button>
                  </div>
                )}
              </div>
              <div className="divide-y divide-stone-100">
                {needsCategoryTasks.map((task, idx) => (
                  <div key={task.id} className={`p-4 flex items-center justify-between hover:bg-stone-50 ${selectedTasksForAssign.includes(task.id) ? 'bg-blue-50' : ''}`}>
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={selectedTasksForAssign.includes(task.id)}
                        onCheckedChange={() => toggleTaskSelection(task.id)}
                        className="data-[state=checked]:bg-blue-600"
                      />
                      <div className="w-10 h-10 rounded-lg bg-stone-50 flex items-center justify-center">
                        <AlertTriangle className="text-stone-600" size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{task.title}</p>
                        <p className="text-xs text-stone-500">
                          Project: {task.project_name} | Created: {formatDate(new Date(task.created_at))}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openAssignDialog(task)}>
                      <Tag size={14} className="mr-1" />
                      Assign Category
                    </Button>
                  </div>
                ))}
                {needsCategoryTasks.length === 0 && (
                  <div className="p-8 text-center text-stone-500">
                    <CheckCircle2 size={40} className="mx-auto mb-3 text-green-400" />
                    <p className="text-green-600 font-medium">All tasks have category assignments!</p>
                    <p className="text-sm">No tasks need category mapping.</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Assign Category Dialog */}
        <Dialog open={assignCategoryDialog} onOpenChange={setAssignCategoryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Category to {selectedTasksForAssign.length} Task(s)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Main Category *</Label>
                <Select 
                  value={assignForm.main_category_id} 
                  onValueChange={(v) => setAssignForm({...assignForm, main_category_id: v, sub_category_id: ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select main category" />
                  </SelectTrigger>
                  <SelectContent>
                    {mainCategories.filter(c => c.status === 'active').map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.code} – {cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sub-Category *</Label>
                <Select 
                  value={assignForm.sub_category_id} 
                  onValueChange={(v) => setAssignForm({...assignForm, sub_category_id: v})}
                  disabled={!assignForm.main_category_id}
                >
                  <SelectTrigger className={!assignForm.main_category_id ? 'bg-stone-100' : ''}>
                    <SelectValue placeholder={assignForm.main_category_id ? "Select sub-category" : "Select main category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredSubCategoriesForAssign().filter(c => c.status === 'active').map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.code} – {cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedTasksForAssign.length > 1 && (
                <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <p className="text-sm text-stone-800">
                    <strong>Bulk Assignment:</strong> This will assign the selected category to all {selectedTasksForAssign.length} selected tasks.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignCategoryDialog(false)}>Cancel</Button>
              <Button 
                onClick={saveAssignment} 
                disabled={assigning || !assignForm.main_category_id || !assignForm.sub_category_id}
                className="bg-stone-800 hover:bg-stone-900"
              >
                {assigning ? 'Assigning...' : `Assign to ${selectedTasksForAssign.length} Task(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Main Category Dialog */}
        <Dialog open={mainCategoryDialog} onOpenChange={setMainCategoryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingMainCategory ? 'Edit Main Category' : 'Add Main Category'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category Code *</Label>
                <Input 
                  value={mainForm.code}
                  onChange={(e) => setMainForm({...mainForm, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., G"
                  maxLength={5}
                  disabled={!!editingMainCategory}
                  className={editingMainCategory ? 'bg-stone-100' : ''}
                />
                {editingMainCategory && <p className="text-xs text-stone-500">Code cannot be changed once created.</p>}
              </div>
              <div className="space-y-2">
                <Label>Category Name *</Label>
                <Input 
                  value={mainForm.name}
                  onChange={(e) => setMainForm({...mainForm, name: e.target.value})}
                  placeholder="e.g., Carpentry & Joinery"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input 
                    type="number"
                    value={mainForm.display_order}
                    onChange={(e) => setMainForm({...mainForm, display_order: parseInt(e.target.value) || 99})}
                    min={1}
                    max={999}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={mainForm.status} onValueChange={(v) => setMainForm({...mainForm, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMainCategoryDialog(false)}>Cancel</Button>
              <Button onClick={saveMainCategory} className="bg-stone-800 hover:bg-stone-900">
                {editingMainCategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Sub-Category Dialog */}
        <Dialog open={subCategoryDialog} onOpenChange={setSubCategoryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSubCategory ? 'Edit Sub-Category' : 'Add Sub-Category'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Main Category *</Label>
                <Select 
                  value={subForm.main_category_id} 
                  onValueChange={handleMainCategoryChange}
                  disabled={!!editingSubCategory}
                >
                  <SelectTrigger className={editingSubCategory ? 'bg-stone-100' : ''}>
                    <SelectValue placeholder="Select main category" />
                  </SelectTrigger>
                  <SelectContent>
                    {mainCategories.filter(c => c.status === 'active').map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.code} – {cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sub-Category Code * <span className="text-stone-400 text-xs">(auto-generated, editable)</span></Label>
                <Input 
                  value={subForm.code}
                  onChange={(e) => setSubForm({...subForm, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., G03"
                  maxLength={10}
                  disabled={!!editingSubCategory}
                  className={editingSubCategory ? 'bg-stone-100' : ''}
                />
                {!editingSubCategory && subForm.main_category_id && (
                  <p className="text-xs text-stone-500">
                    Code must start with: {mainCategories.find(c => c.id === subForm.main_category_id)?.code || ''}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Sub-Category Name *</Label>
                <Input 
                  value={subForm.name}
                  onChange={(e) => setSubForm({...subForm, name: e.target.value})}
                  placeholder="e.g., Modular Kitchen"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input 
                    type="number"
                    value={subForm.display_order}
                    onChange={(e) => setSubForm({...subForm, display_order: parseInt(e.target.value) || 99})}
                    min={1}
                    max={999}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={subForm.status} onValueChange={(v) => setSubForm({...subForm, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSubCategoryDialog(false)}>Cancel</Button>
              <Button onClick={saveSubCategory} className="bg-stone-800 hover:bg-stone-900">
                {editingSubCategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter-359h — Merge Sub-Categories confirmation dialog */}
        <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="merge-sub-categories-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900">Merge sub-categories</DialogTitle>
              <p className="text-sm text-stone-500 mt-1">
                Pick which sub-category becomes the <strong>master</strong>. All tasks + BOQ items
                referencing the others will be re-pointed to the master, and the losers will be
                soft-deleted (their codes stay traceable in old rows).
              </p>
            </DialogHeader>
            <div className="space-y-2 py-2 max-h-[420px] overflow-y-auto">
              {(() => {
                const picked = Array.from(selectedForMerge[mergeMainCategoryId] || []);
                const rows = subCategories.filter((s) => picked.includes(s.id));
                if (rows.length === 0) return <div className="text-sm text-stone-500">Nothing selected.</div>;
                return rows.map((s) => (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                      mergeMasterId === s.id ? 'border-emerald-400 bg-emerald-50/50' : 'border-stone-200 hover:bg-stone-50'
                    }`}
                    data-testid={`merge-master-choice-${s.code}`}
                  >
                    <input
                      type="radio"
                      name="merge-master"
                      value={s.id}
                      checked={mergeMasterId === s.id}
                      onChange={() => setMergeMasterId(s.id)}
                      className="h-4 w-4 accent-emerald-600 shrink-0"
                    />
                    <div className="w-14 h-8 rounded bg-blue-50 flex items-center justify-center font-mono text-xs font-semibold text-blue-700 shrink-0">
                      {s.code}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-800 truncate">{s.name}</div>
                      <div className="text-[11px] text-stone-500">
                        {mergeMasterId === s.id ? 'Master — keeps this code + name' : 'Loser — will be merged into the master'}
                      </div>
                    </div>
                  </label>
                ));
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMergeDialogOpen(false)} disabled={merging} data-testid="merge-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={submitMerge}
                disabled={merging || !mergeMasterId}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="merge-confirm-btn"
              >
                {merging ? 'Merging…' : 'Merge now'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BOQCategoryLibrary;
